import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
} from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { FormFieldComponent } from '../form-field/form-field.component';
import { distinctUntilChanged, takeUntil } from 'rxjs/operators';
import {
  I2gNgTrackingActionBuilder,
  I2gNgTrackingService,
  ITrackingObject,
} from 'i2g-ng-tracking';
import { IIcon } from '../../../icon/icon.component';

@Component({
  selector: 'i2g-checkbox-field',
  templateUrl: 'checkbox-field.component.html',
  styleUrls: ['checkbox-field.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CheckboxFieldComponent
  extends FormFieldComponent
  implements OnChanges, OnDestroy {
  @Input() label: string;
  @Input() labelInnerHtml: string;
  @Input() hideLabel: boolean;
  @Input() fieldId: string;
  @Input() e2eId: string;
  @Input() disabled: boolean;
  @Input() icon: IIcon;
  @Input() description: string;
  @Input() fieldControl: AbstractControl;
  @Input() trackingObject: ITrackingObject;
  @Input() trackingIdentifier: string;
  @Input() trackingAdditionalProperties: any;
  @Input() trackingScreenName: string;

  @Output() toggle: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() clickCheckbox: EventEmitter<Event> = new EventEmitter<Event>();
  @Output() clickIcon: EventEmitter<void> = new EventEmitter<void>();

  private _fieldSub: Subscription;
  isFocused = false;

  constructor(
    changeDetectorRef: ChangeDetectorRef,
    translateService: TranslateService,
    private _trackingService: I2gNgTrackingService,
    private _trackingActionBuilder: I2gNgTrackingActionBuilder
  ) {
    super(changeDetectorRef, translateService);
  }

  private _toggleField() {
    this.fieldControl.setValue(!this.fieldControl.value);
    this.toggle.emit(this.fieldControl.value);
  }

  toggleCheckbox(event: MouseEvent) {
    if (!this.disabled) {
      if (this.clickCheckbox.observers.length) {
        this.clickCheckbox.emit(event);
      } else {
        this._toggleField();
      }
    }
  }

  onKeyDown(event: KeyboardEvent) {
    if (event.key === ' ' && this.isFocused) {
      this._toggleField();
      event.preventDefault();
    }
  }

  onFocus() {
    this.isFocused = true;
  }

  onBlur() {
    this.isFocused = false;
  }

  ngOnChanges(changes) {
    const trackingObject = changes.trackingObject
      ? changes.trackingObject.currentValue
      : this.trackingObject;
    const trackingIdentifier = changes.trackingIdentifier
      ? changes.trackingIdentifier.currentValue
      : this.trackingIdentifier;
    const trackingScreenName = changes.trackingScreenName
      ? changes.trackingScreenName.currentValue
      : this.trackingScreenName;

    if (
      this.fieldControl &&
      trackingObject &&
      trackingIdentifier &&
      trackingScreenName
    ) {
      if (this._fieldSub) {
        this._fieldSub.unsubscribe();
      }

      this._fieldSub = this.fieldControl.valueChanges
        .pipe(distinctUntilChanged(), takeUntil(this.destroyed))
        .subscribe((value) => {
          const trackingAction = value
            ? this._trackingActionBuilder.buildCheckboxChecked(
                trackingIdentifier,
                trackingScreenName
              )
            : this._trackingActionBuilder.buildCheckboxUnchecked(
                trackingIdentifier,
                trackingScreenName
              );

          this._trackingService.trackEvent(
            trackingObject,
            trackingAction,
            this.trackingAdditionalProperties
          );
        });
    }
  }

  ngOnDestroy() {
    if (this._fieldSub) {
      this._fieldSub.unsubscribe();
    }

    super.ngOnDestroy();
  }
}
